import { GRADE_TOKENS } from "./geography";

// src/config/plans.js
//
// SINGLE SOURCE OF TRUTH for subscription plans on the client.
//
// Note what is NOT in this file: Stripe price IDs. The client never sees them.
// It sends a `planKey`; the Cloud Function resolves that to a live Price via
// Stripe lookup key. That means a price change is a Stripe Dashboard edit,
// not a redeploy.
//
// This replaces the subscription half of data/signup/product-catalog.js.
// That catalog's ~250 buy.stripe.com Payment Links create anonymous purchases
// with no Firebase UID attached, so the webhook can never grant access from
// them. Keep the catalog for its copy (titles, blurbs, toolsIncluded) — its
// stripeMonthly/stripeYearly fields are now dead weight.
//
// ---------------------------------------------------------------------------
// THE PRICE LABELS BELOW ARE NOT THE PRICE. STRIPE IS.
//
// `priceLabel` is display text. The amount actually charged comes from the
// Stripe Price carrying the matching lookup key (educator_monthly,
// student_yearly, and so on — see functions/stripeSubscriptions.js). Nothing
// checks that the two agree, so if you change a price in Stripe you must
// change the string here by hand or the app will quote one number and bill
// another.
//
// Current intent: educator $15/mo and $150/yr nationally; student and parent
// $7.99/mo and $79.90/yr. Confirm the student figure against the live Stripe
// Price before announcing it anywhere.
//
// Worth knowing: data/signup/product-catalog.js still carries its own
// per-state prices, and they disagree — DC educator is listed there at
// $9.99/mo, for instance. Those labels are shown on the products page. They
// need reconciling with the flat pricing here, or a customer sees $9.99 and
// gets billed $15.
// ---------------------------------------------------------------------------

export const BILLING = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

// Roles a person can register as. These are INTENT, not granted access —
// see ROLE_GRANTS below for why that distinction matters.
export const SIGNUP_ROLES = {
  EDUCATOR: "educator",
  STUDENT: "student",
  PARENT: "parent",
  DISTRICT: "district",
};

export const PLANS = {
  educator_monthly: {
    key: "educator_monthly",
    role: SIGNUP_ROLES.EDUCATOR,
    billing: BILLING.MONTHLY,
    title: "Educator Access",
    priceLabel: "$15 / month per seat",
    seatType: "teacher",
    allowsMultipleSeats: true,
    blurb: "Just you: your full teacher portal, classroom tools and student tracking. Students join free (first 3 topics) or with their own plan.",
  },
  educator_yearly: {
    key: "educator_yearly",
    role: SIGNUP_ROLES.EDUCATOR,
    billing: BILLING.YEARLY,
    title: "Educator Access",
    priceLabel: "$150 / year per seat",
    seatType: "teacher",
    allowsMultipleSeats: true,
    savingsNote: "Two months free vs. monthly",
    blurb: "Just you: your full teacher portal, classroom tools and student tracking. Students join free (first 3 topics) or with their own plan.",
  },
  student_monthly: {
    key: "student_monthly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.MONTHLY,
    title: "Student Access",
    priceLabel: "$7.99 / month per seat",
    seatType: "student",
    allowsMultipleSeats: true,
    blurb: "Music, video, quizzes, and games for the selected grade.",
  },
  student_yearly: {
    key: "student_yearly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.YEARLY,
    title: "Student Access",
    priceLabel: "$79.90 / year per seat",
    seatType: "student",
    allowsMultipleSeats: true,
    savingsNote: "Two months free vs. monthly",
    blurb: "Music, video, quizzes, and games for the selected grade.",
  },
  parent_monthly: {
    key: "parent_monthly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.MONTHLY,
    title: "Parent Access",
    priceLabel: "$7.99 / month",
    seatType: "single",
    allowsMultipleSeats: false,
    blurb: "One parent account plus one child's learning space.",
  },
  parent_yearly: {
    key: "parent_yearly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.YEARLY,
    title: "Parent Access",
    priceLabel: "$79.90 / year",
    seatType: "single",
    allowsMultipleSeats: false,
    savingsNote: "Two months free vs. monthly",
    blurb: "One parent account plus one child's learning space.",
  },

  // =========================================================================
  // GRADES 6-12 — priced by the course, because that's how school works
  // =========================================================================
  // Elementary keeps the plans above unchanged. One teacher covers every
  // subject, every child in the room is on the same page, and "all subjects"
  // is the only sensible unit. $7.99 buys the grade.
  //
  // From 6th, students change rooms and teachers by subject, and from 9th the
  // subject itself stops being grade-shaped — see src/data/courses.js. A
  // family who wants help with Chemistry and nothing else is a real customer,
  // and $4.99 is a far easier yes than $7.99 for one class.
  //
  // WHY THE BUNDLE IS $12.99 AND NOT 4 x $4.99
  // ------------------------------------------
  // Four subjects at $4.99 is $19.96 — meaning a family pays $7.99 for a 5th
  // grader and $20 for a 9th grader, same brand, same month. That comparison
  // gets made out loud, and it doesn't go well.
  //
  // At $12.99 the bundle beats three singles, so anyone taking two or more
  // subjects lands there on their own arithmetic. The single-subject price
  // is the way IN, not the standard price.
  //
  // Both are one Stripe subscription. The difference is `scope` on the
  // subscription document, not the number of subscriptions.
  student_subject_monthly: {
    key: "student_subject_monthly",
    lookupKey: "student_subject_monthly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.MONTHLY,
    band: "secondary",
    scope: "subject",
    title: "One Subject",
    priceLabel: "$4.99 / month per subject",
    seatType: "student",
    // SINGLE SEAT ONLY, and the Stripe `quantity` on this plan counts
    // COURSES, not people.
    //
    // Those two facts are the same decision. A teacher buying 30 seats does
    // not want to pick a course list for each of 30 children, and a schema
    // that multiplies seats by courses has no sane checkout screen. So the
    // line is drawn here: single-subject is for one student picking their
    // own courses; classroom and family bulk buying uses All Subjects.
    allowsMultipleSeats: false,
    quantityMeans: "courses",
    blurb: "Everything in one course — music, video, quizzes, and games.",
  },
  student_subject_yearly: {
    key: "student_subject_yearly",
    lookupKey: "student_subject_yearly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.YEARLY,
    band: "secondary",
    scope: "subject",
    title: "One Subject",
    priceLabel: "$49.90 / year per subject",
    seatType: "student",
    allowsMultipleSeats: false,
    quantityMeans: "courses",
    savingsNote: "Two months free vs. monthly",
    blurb: "Everything in one course — music, video, quizzes, and games.",
  },
  student_all_monthly: {
    key: "student_all_monthly",
    lookupKey: "student_all_monthly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.MONTHLY,
    band: "secondary",
    scope: "all",
    title: "All Subjects",
    priceLabel: "$12.99 / month",
    seatType: "student",
    allowsMultipleSeats: true,
    savingsNote: "Cheaper than three subjects",
    blurb: "Every course in the student's schedule, and any they add later.",
  },
  student_all_yearly: {
    key: "student_all_yearly",
    lookupKey: "student_all_yearly",
    role: SIGNUP_ROLES.STUDENT,
    billing: BILLING.YEARLY,
    band: "secondary",
    scope: "all",
    title: "All Subjects",
    priceLabel: "$129.90 / year",
    seatType: "student",
    allowsMultipleSeats: true,
    savingsNote: "Two months free vs. monthly",
    blurb: "Every course in the student's schedule, and any they add later.",
  },

  // Parent mirrors student at 6-12 exactly as it does at K-5. A parent buying
  // for their own child is making the same purchase; only the account shape
  // differs.
  parent_subject_monthly: {
    key: "parent_subject_monthly",
    lookupKey: "parent_subject_monthly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.MONTHLY,
    band: "secondary",
    scope: "subject",
    title: "One Subject",
    priceLabel: "$4.99 / month per subject",
    seatType: "single",
    allowsMultipleSeats: false,
    quantityMeans: "courses",
    blurb: "Everything in one of your child's courses.",
  },
  parent_subject_yearly: {
    key: "parent_subject_yearly",
    lookupKey: "parent_subject_yearly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.YEARLY,
    band: "secondary",
    scope: "subject",
    title: "One Subject",
    priceLabel: "$49.90 / year per subject",
    seatType: "single",
    allowsMultipleSeats: false,
    quantityMeans: "courses",
    savingsNote: "Two months free vs. monthly",
    blurb: "Everything in one of your child's courses.",
  },
  parent_all_monthly: {
    key: "parent_all_monthly",
    lookupKey: "parent_all_monthly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.MONTHLY,
    band: "secondary",
    scope: "all",
    title: "All Subjects",
    priceLabel: "$12.99 / month",
    seatType: "single",
    allowsMultipleSeats: false,
    savingsNote: "Cheaper than three subjects",
    blurb: "Every course your child is taking, and any they add later.",
  },
  parent_all_yearly: {
    key: "parent_all_yearly",
    lookupKey: "parent_all_yearly",
    role: SIGNUP_ROLES.PARENT,
    billing: BILLING.YEARLY,
    band: "secondary",
    scope: "all",
    title: "All Subjects",
    priceLabel: "$129.90 / year",
    seatType: "single",
    allowsMultipleSeats: false,
    savingsNote: "Two months free vs. monthly",
    blurb: "Every course your child is taking, and any they add later.",
  },
};

// ===========================================================================
// CLASSROOM PLANS — one teacher + their class, priced by class size
// ===========================================================================
// The Teacher plan ($15) covers the teacher only; each student pays their own
// way. A Classroom plan covers the teacher AND every student on their roster
// up to the size bought. Yearly = 10 months' price (two months free).
// Amounts are charged by functions/planAccess.js (CLASSROOM_PLANS) — keep the
// two in step. School and District plans are quoted / invoiced (PO).
export const FREE_TRIAL_DAYS = 60;
export const TRIAL_STUDENT_CAP = 5;
export const TEACHER_STUDENT_CAP = 35;

export const CLASSROOM_BANDS = [
  { size: 10, monthly: 25, yearly: 250, blurb: "Small groups, tutoring and pull-outs." },
  { size: 20, monthly: 35, yearly: 350, blurb: "A typical elementary class.", popular: true },
  { size: 35, monthly: 45, yearly: 450, blurb: "Big classes and combined groups." },
];

CLASSROOM_BANDS.forEach((b) => {
  ["monthly", "yearly"].forEach((billing) => {
    const key = `classroom${b.size}_${billing}`;
    PLANS[key] = {
      key,
      role: SIGNUP_ROLES.EDUCATOR,
      billing,
      classroom: true,
      studentCap: b.size,
      title: `Classroom · up to ${b.size} students`,
      priceLabel: billing === "yearly" ? `$${b.yearly} / year` : `$${b.monthly} / month`,
      seatType: "teacher",
      allowsMultipleSeats: false,
      ...(billing === "yearly" ? { savingsNote: "Two months free vs. monthly" } : {}),
      blurb: `You plus up to ${b.size} students — every student gets full access and earns coins.`,
    };
  });
});

export const isClassroomPlan = (planKey) => Boolean(PLANS[planKey]?.classroom);

// Educator stays flat at $15 in both bands, deliberately.
//
// A secondary teacher only ever needs one subject — Mr. Allen teaches
// science, full stop — so charging them per subject would mean charging every
// educator $4.99 and losing money against the $15 they pay today. And a flat
// plan means a teacher who picks up a Chemistry section in January doesn't
// hit a paywall in the middle of the school year. The classroom tools, not
// the content, are what the $15 is for.

export const SCOPE = {
  ALL: "all",
  SUBJECT: "subject",
};

export const BAND = {
  ELEMENTARY: "elementary",
  SECONDARY: "secondary",
};

const SECONDARY_GRADE_TOKENS = new Set([
  "6th", "7th", "8th", "9th", "10th", "11th", "12th",
]);

const ELEMENTARY_GRADE_TOKENS = new Set([
  "prek", "k", "1st", "2nd", "3rd", "4th", "5th",
]);

/**
 * Which pricing band a grade — or a course track token — belongs to.
 *
 * Accepts a course token as well as a grade, because by the time this is
 * called the value may already have been resolved to a track (see
 * src/data/courses.js). Courses only exist from 6th up, so anything that
 * isn't a recognised elementary grade is secondary.
 */
export function gradeBand(grade) {
  const token = gradeToken(grade);
  if (!token) return BAND.ELEMENTARY;
  if (ELEMENTARY_GRADE_TOKENS.has(token)) return BAND.ELEMENTARY;
  if (SECONDARY_GRADE_TOKENS.has(token)) return BAND.SECONDARY;
  return BAND.SECONDARY; // a course token
}

// District/PO never touches Stripe Checkout — it routes to your existing
// /purchase-order request flow and is granted manually after approval.
export const DISTRICT_PLAN = {
  key: "district_po",
  role: SIGNUP_ROLES.DISTRICT,
  title: "School / District Purchase Order",
  priceLabel: "Invoiced — contact us",
  paymentType: "purchase_order",
  route: "/purchase-order",
  blurb: "Site license across your school or district. Access after review.",
};

/**
 * Plans available to a given signup role, in display order.
 */
export function plansForRole(role) {
  return Object.values(PLANS).filter((p) => p.role === role && !p.classroom);
}

/**
 * role + billing -> planKey.
 *
 * Signature takes state and grade even though they don't affect the result
 * today. Every plan is currently flat-rate nationally. If you later decide
 * NC should be $9.99 while VA is $15, this is the ONE function that changes —
 * you add `nc_educator_monthly` to PLANS and branch here, and nothing else in
 * the app has to learn about it.
 */
export function resolvePlanKey({ role, billing, grade, scope /*, state */ }) {
  if (!role || !billing) return null;

  // Educator and district are flat in both bands — see the note above PLANS.
  if (role === SIGNUP_ROLES.EDUCATOR || role === SIGNUP_ROLES.DISTRICT) {
    const flat = `${role}_${billing}`;
    return PLANS[flat] ? flat : null;
  }

  // NO GRADE MEANS ELEMENTARY, DELIBERATELY.
  //
  // Every caller written before the 6-12 tiers existed passes { role,
  // billing } and nothing else, and each one must keep resolving to exactly
  // the plan it resolved to yesterday.
  //
  // It also fails in the safe direction. Guessing elementary undercharges a
  // secondary customer by a few dollars, which is a support email. Guessing
  // secondary-all overcharges an elementary one, which is a chargeback and a
  // review. The warning is here so this shows up in a console before it
  // shows up in Stripe.
  if (grade === undefined || grade === null || grade === "") {
    console.warn(
      "[plans] resolvePlanKey called without a grade — defaulting to " +
        "elementary pricing. Pass `grade` so 6-12 gets its own tier."
    );
  }

  if (gradeBand(grade) === BAND.ELEMENTARY) {
    const key = `${role}_${billing}`;
    return PLANS[key] ? key : null;
  }

  const scopeToken = scope === SCOPE.SUBJECT ? SCOPE.SUBJECT : SCOPE.ALL;
  const key = `${role}_${scopeToken}_${billing}`;
  return PLANS[key] ? key : null;
}

/**
 * The plans to show on the plan step for a given role and grade.
 *
 * Elementary returns the two existing all-subject plans. Secondary returns
 * four: one subject and all subjects, monthly and yearly.
 */
export function plansForRoleAndGrade(role, grade) {
  const band = gradeBand(grade);
  return Object.values(PLANS).filter((p) => {
    if (p.role !== role) return false;
    if (p.classroom) return false; // shown in their own section on the plan page
    if (band === BAND.ELEMENTARY) return !p.band;
    return p.band === BAND.SECONDARY;
  });
}

// ===========================================================================
// ENTITLEMENTS — what a single-subject subscription actually unlocks
// ===========================================================================
// The webhook writes the purchased scope onto the subscription document:
//
//     subscriptions/{uid} = {
//       active, status, planKey, currentPeriodEnd,
//       scope: "all" | "subject",
//       courses: { "algebra-1": true, "biology-1": true },
//     }
//
// `courses` is keyed by TRACK TOKEN — the same token that addresses the
// portal layout (src/data/courses.js). One vocabulary for content and for
// billing means there's no mapping table to fall out of sync.
//
// AN ABSENT `scope` MEANS FULL ACCESS.
// Every subscription written before this change has no scope field, and
// every one of those customers bought all subjects. Treating a missing field
// as "subject" would lock out every existing paying customer the moment this
// deploys. The default has to be the permissive one here, and it's the only
// place in this codebase where that's true — which is exactly why it's
// spelled out rather than left to a `?.` and a guess.

/**
 * Does this subscription cover this course?
 *
 * @param {object|null} subscription the subscriptions/{uid} document
 * @param {string} track course or grade token, e.g. "algebra-1" or "5th"
 */
export function subscriptionCoversCourse(subscription, track) {
  if (!subscription) return false;
  if (subscription.scope !== SCOPE.SUBJECT) return true;
  if (!track) return false;
  return Boolean(subscription.courses?.[String(track).toLowerCase()]);
}

/**
 * The track tokens a subject-scoped subscription has paid for. Empty array
 * for an all-subjects subscription — "everything" isn't a list.
 */
export function purchasedCourses(subscription) {
  if (!subscription || subscription.scope !== SCOPE.SUBJECT) return [];
  return Object.entries(subscription.courses || {})
    .filter(([, on]) => on === true)
    .map(([token]) => token);
}

// "1" -> "1st", "Kindergarten" -> "k" — the token used in URLs, layout IDs
// and role strings.
//
// The old version was a five-entry map covering 1-5 and nothing else. It fell
// through to `String(g)` for anything unrecognised, so "Kindergarten" became
// the literal token "Kindergarten" and 6th-12th passed through as whatever
// the form happened to store. A role of "va-Kindergarten-educator" matches no
// route, and the account it belongs to can't reach any content.
//
// GRADE_TOKENS in config/geography.js is now the single mapping, so the
// signup form, the role composer and the layout seeder can't disagree.


export const gradeToken = (g) => {
  const raw = String(g ?? "").trim();
  if (!raw) return "";

  // Exact label: "Kindergarten", "3rd", "10th".
  if (GRADE_TOKENS[raw]) return GRADE_TOKENS[raw];

  // Bare number from an older form or an import: "3" -> "3rd".
  const n = Number.parseInt(raw, 10);
  if (!Number.isNaN(n) && n >= 1 && n <= 12) {
    const suffix =
      n % 100 >= 11 && n % 100 <= 13
        ? "th"
        : { 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th";
    return `${n}${suffix}`;
  }

  // Already a token ("3rd", "k", "prek"), or something we don't recognise —
  // lower-cased so casing alone can't produce two different role strings.
  return raw.toLowerCase();
};

/**
 * The role string your PrivateRoute actually checks, e.g. "va-5th-educator".
 * Composed server-side by the webhook after payment clears — never by the
 * client. Exported here so the client can PREVIEW what someone will get.
 */
export function composeGrantedRole({ state, grade, role }) {
  if (!state || !grade || !role) return null;
  return `${String(state).toLowerCase()}-${gradeToken(grade)}-${role}`;
}

/**
 * The role written at registration, before any money changes hands.
 *
 * This value is deliberately ugly and deliberately contains none of the
 * substrings "educator", "student", "admin", or "administrator". PrivateRoute
 * matches with `currentUserRole.includes(role)`, so a placeholder like
 * "pending-va-5th-educator" would sail straight through an educator gate for
 * free. "unpaid" cannot.
 */
export const UNPAID_ROLE = "unpaid";

export const MAX_SEATS = 500;

export default PLANS;
